/**
 * Namespaced browser storage.
 *
 * On order.app2food.com the prefix is "" — keys stay exactly as they are today,
 * so existing customers keep their carts and sessions.
 *
 * On a client domain the prefix is "a2f:{storeId}:", which does two things:
 * it stops us colliding with whatever the client's own site already keeps in
 * localStorage, and it keeps two stores embedded on one domain apart.
 *
 * Every method is failure-tolerant: Safari private mode and cookie-blocking
 * extensions throw on access rather than returning null, and an ordering flow
 * must not die because of it.
 */

import { getEmbedConfig } from "./config";

function key(name: string): string {
  return getEmbedConfig().storagePrefix + name;
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export const local = {
  getItem(name: string): string | null {
    return safe(() => window.localStorage.getItem(key(name)), null);
  },
  setItem(name: string, value: string): void {
    safe(() => window.localStorage.setItem(key(name), value), undefined);
  },
  removeItem(name: string): void {
    safe(() => window.localStorage.removeItem(key(name)), undefined);
  },
};

export const session = {
  getItem(name: string): string | null {
    return safe(() => window.sessionStorage.getItem(key(name)), null);
  },
  setItem(name: string, value: string): void {
    safe(() => window.sessionStorage.setItem(key(name), value), undefined);
  },
  removeItem(name: string): void {
    safe(() => window.sessionStorage.removeItem(key(name)), undefined);
  },
};
